import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[var(--main-bg)]" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto p-6 sm:p-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] mb-8 transition-colors"
          data-testid="privacy-back-btn"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back</span>
        </button>

        <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Privacy Policy
        </h1>
        <p className="text-sm text-[var(--text-secondary)] font-mono uppercase tracking-wider mb-10">
          Last updated: April 5, 2026
        </p>

        <div className="space-y-8 text-[var(--text-primary)]">
          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>1. Introduction</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              VAULT ("we," "our," "us") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains what information we collect, how we use it, and your rights regarding your data. By using VAULT, you agree to the practices described in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>2. Information We Collect</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-4">
              <div>
                <h3 className="font-bold text-[var(--text-primary)] mb-1">Information you provide:</h3>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li><strong>Account data:</strong> Name, email address, and password (hashed and encrypted)</li>
                  <li><strong>Profile data:</strong> Bio, avatar preferences</li>
                  <li><strong>Content:</strong> Ideas, titles, categories, tags, and any text you enter</li>
                  <li><strong>Payment data:</strong> Processed directly by Stripe. We do NOT store your credit card numbers, CVV, or full card details</li>
                  <li><strong>Support requests:</strong> Messages submitted through our FAQ/support system</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-[var(--text-primary)] mb-1">Information collected automatically:</h3>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li><strong>Authentication tokens:</strong> Secure HTTP-only cookies for session management</li>
                  <li><strong>Usage data:</strong> Feature usage patterns, idea counts, and login timestamps for analytics</li>
                  <li><strong>Device info:</strong> Browser type and version (via standard HTTP headers)</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>3. How We Use Your Information</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)]">
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong>Provide the Service:</strong> Store and display your ideas, enable search, categorization, and export</li>
                <li><strong>AI Enhancement:</strong> When you use AI Enhance, your idea title and content are sent to OpenAI's API for processing. This data is not used to train AI models</li>
                <li><strong>Authentication & Security:</strong> Verify your identity, manage sessions, and enforce two-factor authentication</li>
                <li><strong>Payment processing:</strong> Facilitate subscriptions through Stripe</li>
                <li><strong>Communications:</strong> Send transactional emails (welcome, subscription confirmation, security alerts for 2FA changes)</li>
                <li><strong>Analytics:</strong> Aggregate, anonymized usage data to improve the Service (admin dashboard analytics)</li>
                <li><strong>Team collaboration:</strong> Share ideas with team members you explicitly invite</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>4. How We Protect Your Data</h2>
            <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-5 text-sm leading-relaxed space-y-2">
              <p><strong>Passwords:</strong> Hashed using bcrypt (one-way encryption). We cannot see your password.</p>
              <p><strong>2FA secrets:</strong> Encrypted using Fernet symmetric encryption (AES-128-CBC).</p>
              <p><strong>Backup codes:</strong> Individually hashed with bcrypt before storage.</p>
              <p><strong>Sessions:</strong> HTTP-only, secure cookies with short expiration times.</p>
              <p><strong>Database:</strong> Access restricted to authenticated application processes only.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>5. Third-Party Services</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-3">
              <p>We use the following third-party services that may process your data:</p>
              <div className="space-y-3">
                <div className="bg-[var(--surface)] border border-[var(--border-default)] p-4">
                  <p className="font-bold text-[var(--text-primary)]">Stripe (Payments)</p>
                  <p>Processes payments securely. Subject to <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--obsidian)]">Stripe's Privacy Policy</a>.</p>
                </div>
                <div className="bg-[var(--surface)] border border-[var(--border-default)] p-4">
                  <p className="font-bold text-[var(--text-primary)]">OpenAI (AI Enhancement)</p>
                  <p>Processes idea content for AI enhancement when you explicitly request it. Subject to <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--obsidian)]">OpenAI's Privacy Policy</a>. API data is not used for model training.</p>
                </div>
                <div className="bg-[var(--surface)] border border-[var(--border-default)] p-4">
                  <p className="font-bold text-[var(--text-primary)]">Resend (Email)</p>
                  <p>Sends transactional emails. Subject to <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--obsidian)]">Resend's Privacy Policy</a>.</p>
                </div>
              </div>
              <p>We do not sell, rent, or share your personal information with any third parties for marketing purposes.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>6. Your Rights</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-3">
              <p>You have the following rights regarding your data:</p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong>Access:</strong> View all data associated with your account via your Profile page</li>
                <li><strong>Export:</strong> Download all your ideas in Markdown or JSON format at any time from the Dashboard</li>
                <li><strong>Correction:</strong> Update your profile information at any time</li>
                <li><strong>Deletion:</strong> Request complete deletion of your account and all associated data. Contact us via the Support page</li>
                <li><strong>Portability:</strong> Export your data in standard formats (JSON, Markdown) to transfer to another service</li>
                <li><strong>Withdraw consent:</strong> You may stop using the Service at any time</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>7. Data Retention</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-3">
              <p><strong>Active accounts:</strong> We retain your data for as long as your account is active.</p>
              <p><strong>Deleted accounts:</strong> Upon account deletion, all personal data and ideas are permanently removed within 30 days.</p>
              <p><strong>Email logs:</strong> Transactional email records are retained for 90 days for troubleshooting, then deleted.</p>
              <p><strong>Payment records:</strong> Retained as required by applicable tax and financial regulations.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>8. Cookies</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-3">
              <p>VAULT uses the following cookies:</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li><strong>access_token:</strong> HTTP-only session cookie for authentication (expires in 15 minutes)</li>
                <li><strong>refresh_token:</strong> HTTP-only cookie for session renewal (expires in 7 days)</li>
                <li><strong>Theme preference:</strong> Local storage for dark/light mode (not a cookie, stays on your device)</li>
              </ul>
              <p>We do not use tracking cookies, advertising cookies, or any third-party analytics cookies.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>9. Children's Privacy</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              VAULT is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that a child under 13 has provided us with personal data, we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>10. GDPR (European Users)</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-3">
              <p>If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation:</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>Right to access, rectify, or erase your personal data</li>
                <li>Right to restrict or object to processing</li>
                <li>Right to data portability (export feature available)</li>
                <li>Right to lodge a complaint with a supervisory authority</li>
              </ul>
              <p>Our legal basis for processing is: (a) your consent, (b) performance of a contract (providing the Service), and (c) our legitimate interests in improving the Service.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>11. CCPA (California Users)</h2>
            <div className="text-sm leading-relaxed text-[var(--text-secondary)] space-y-3">
              <p>If you are a California resident, you have the right to:</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>Know what personal information we collect and how it's used</li>
                <li>Request deletion of your personal information</li>
                <li>Opt-out of the sale of personal information (we do NOT sell your data)</li>
                <li>Non-discrimination for exercising your privacy rights</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>12. Changes to This Policy</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through a notice in the Service. Your continued use of VAULT after changes are posted constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>13. Contact Us</h2>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              For privacy-related questions or to exercise your rights, contact us through the Support page within the app or email us at the address provided in your account settings.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
